import { useMemo, useState } from 'react';
import { RICHTEXT_SETTINGS, ViewProps } from './table.constants';
import { useActivityBuilderTheme } from 'approot/activity-builder/theme/activity-builder-theme';
import { getExtensions } from 'approot/shared/richtext/richtext.utils';
import {
  getEditableCellsRowHeight,
  getTableHeaderAndBodyRows,
  isCellEmpty,
} from './table-view.utils';
import { getComponentSelectorId } from '../shared/logic/get-component-selector-id';
import { ColGroupsView } from './table-common';
import TableViewField from './table-view-field';
import * as Styled from './table.style';
import { getRichtextValue } from '../shared/logic/get-richtext-value';
import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';
import { TableAnswer } from './table-view.constants';

const TableViewFields = (
  props: ViewProps & {
    canRespond: boolean;
    answers?: AnswerFragment[];
    updateAnswer: (answer: TableAnswer) => void;
    onFocus?: () => void;
  }
) => {
  const { id, properties, answers, canRespond, updateAnswer, onFocus } = props;

  const [tableWrapperRef, setTableWrapperRef] = useState<HTMLDivElement | null>(
    null
  );
  const { theme } = useActivityBuilderTheme();

  const extensions = useMemo(() => getExtensions(RICHTEXT_SETTINGS), []);

  const table = properties.table;

  const { tableHeaderRow, tableBodyRows } = useMemo(
    () => getTableHeaderAndBodyRows(table),
    [table]
  );

  const headerTextAreaRowHeight = useMemo(() => {
    return getEditableCellsRowHeight(tableHeaderRow?.cells || []);
  }, [tableHeaderRow]);

  return (
    <Styled.TableWrapper
      ref={setTableWrapperRef}
      id={getComponentSelectorId(id)}
    >
      {table.isReadOnly && onFocus && (
        <Styled.TableReadOnlyTrigger
          type="button"
          aria-label="Show text to speech controls for table"
          onClick={onFocus}
        />
      )}
      <Styled.Table cellSpacing="0" key={`rows-${table.rows.length}`}>
        <ColGroupsView
          columnWidths={table.columnWidths}
          containerElement={tableWrapperRef}
        />
        {tableHeaderRow && (
          <thead>
            <Styled.TableRow data-selector="present-table-header-row">
              {tableHeaderRow.cells.map((header, index) => {
                const cellKey = `row-1-column-${index}`;
                const editorRows = getEditableCellsRowHeight(
                  tableHeaderRow.cells
                );
                return (
                  <Styled.TableCell $highlight key={cellKey}>
                    <>
                      {canRespond &&
                      !table.isReadOnly &&
                      isCellEmpty(header.value) ? (
                        <TableViewField
                          key={`${cellKey}-text`}
                          highlight={table.showColumnHeaders && index === 0}
                          rowIndex={0}
                          cellIndex={index}
                          editorRowHeight={editorRows}
                          answer={
                            answers?.find(
                              ans =>
                                ans.answerReferences?.row === 0 &&
                                ans.answerReferences.col === index
                            ) || null
                          }
                          updateAnswer={updateAnswer}
                          onFocus={onFocus}
                        />
                      ) : (
                        <Styled.TableCellView
                          data-selector="present-table-header-cell"
                          $highlight
                          $baseFontSize={theme.font.medium.size}
                          $baseLineHeight={theme.font.small.lineHeight}
                          $rows={headerTextAreaRowHeight}
                          dangerouslySetInnerHTML={{
                            __html: getRichtextValue(header.value, extensions),
                          }}
                        />
                      )}
                    </>
                  </Styled.TableCell>
                );
              })}
            </Styled.TableRow>
          </thead>
        )}

        <tbody>
          {tableBodyRows.map((row, tableBodyIndex) => {
            const rowIndex = table.showRowHeaders
              ? tableBodyIndex + 1
              : tableBodyIndex;
            const editorRows = getEditableCellsRowHeight(row.cells);
            return (
              <Styled.TableRow
                data-selector="present-table-row"
                key={`row-${rowIndex}`}
              >
                {row.cells.map((cell, cellIndex) => {
                  const cellKey = `row-${rowIndex}-cell-${cellIndex}`;
                  return (
                    <Styled.TableCell
                      key={cellKey}
                      $highlight={table.showColumnHeaders && cellIndex === 0}
                    >
                      {canRespond &&
                      !table.isReadOnly &&
                      isCellEmpty(cell.value) ? (
                        <TableViewField
                          key={`${cellKey}-text`}
                          highlight={table.showColumnHeaders && cellIndex === 0}
                          rowIndex={rowIndex}
                          cellIndex={cellIndex}
                          editorRowHeight={editorRows}
                          answer={
                            answers?.find(
                              ans =>
                                ans.answerReferences?.row === rowIndex &&
                                ans.answerReferences.col === cellIndex
                            ) || null
                          }
                          updateAnswer={updateAnswer}
                          onFocus={onFocus}
                        />
                      ) : (
                        <Styled.TableCellView
                          data-selector={
                            table.showColumnHeaders && cellIndex === 0
                              ? 'present-table-header-cell'
                              : 'present-table-cell'
                          }
                          $highlight={
                            table.showColumnHeaders && cellIndex === 0
                          }
                          $baseFontSize={theme.font.medium.size}
                          $baseLineHeight={theme.font.small.lineHeight}
                          $rows={editorRows}
                          dangerouslySetInnerHTML={{
                            __html: getRichtextValue(cell.value, extensions),
                          }}
                        />
                      )}
                    </Styled.TableCell>
                  );
                })}
              </Styled.TableRow>
            );
          })}
        </tbody>
      </Styled.Table>
    </Styled.TableWrapper>
  );
};

export default TableViewFields;
